/**
 * Сборка занятия режимов «Developer Pack» и «Email and Web Pack».
 *
 * Упражнение собирается из фрагментов, посильных при открытых токенах, с
 * предпочтением тем, где есть токен текущей группы: занятие существует ради
 * него. Посильных фрагментов на ранних группах меньше, чем элементов в
 * упражнении, поэтому набор повторяется по кругу — короткое занятие из трёх
 * строк не тренирует ничего.
 *
 * Сборка детерминирована по seed: то же занятие воспроизводится в тестах
 * посимвольно (решение AD-012).
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { findTokenSpans } from '@type2x/typing-engine';
import { createRng, type Rng } from '@type2x/exercise-generator';

import type {
  PackFragment,
  PackGroup,
  PackMode,
  PacksExercise,
  PacksPlan,
  PacksSnapshot,
} from '../contracts/packs.js';
import { HINT_LEVEL, ITEMS_PER_EXERCISE, PACK_LAYOUT } from '../contracts/packs.js';
import {
  completedGroups,
  currentGroup,
  packGroups,
  packUnit,
  shiftHints,
  unlockedTokens,
} from './groups.js';
import { affordableFragments } from './material.js';

/**
 * Элементы упражнения.
 *
 * Сначала идут фрагменты с токеном текущей группы, затем остальные
 * посильные: новое тренируется, знакомое не даёт ему остаться в отрыве.
 * Если посильных фрагментов меньше, чем нужно элементов, набор повторяется —
 * порядок при этом сохраняется, поэтому занятие остаётся воспроизводимым.
 */
function fragmentItems(
  rng: Rng,
  affordable: readonly PackFragment[],
  group: PackGroup | null,
  count: number,
): string[] {
  if (affordable.length === 0) return [];

  const wanted =
    group === null
      ? []
      : affordable.filter((fragment) => group.tokens.some((t) => fragment.tokens.includes(t)));

  const rest = affordable.filter((fragment) => !wanted.includes(fragment));
  const ordered = [...rng.shuffle(wanted), ...rng.shuffle(rest)];

  return Array.from({ length: count }, (_, index) => ordered[index % ordered.length]!.text);
}

/** Собирает упражнение по открытым токенам режима. */
function buildExercise(
  mode: PackMode,
  seed: string,
  group: PackGroup | null,
  unlocked: readonly string[],
): PacksExercise {
  const rng = createRng(seed);
  const items = fragmentItems(
    rng,
    affordableFragments(mode, unlocked),
    group,
    ITEMS_PER_EXERCISE[mode],
  );

  return { seed, items, text: items.join(' ') };
}

/**
 * Строит занятие режима по текущему состоянию навыка.
 *
 * @param snapshot состояние навыка;
 * @param mode режим занятия;
 * @param seed зерно генератора: одно и то же занятие воспроизводимо.
 */
export function buildPacksPlan(snapshot: PacksSnapshot, mode: PackMode, seed: string): PacksPlan {
  const group = currentGroup(snapshot, mode);
  const unlocked = unlockedTokens(snapshot, mode);
  const exercise = buildExercise(mode, seed, group, unlocked);

  // Единицы занятия — только токены, действительно попавшие в текст: итог не
  // должен выдавать за тренировку то, чего в упражнении не было.
  const present = [...new Set(findTokenSpans(exercise.text, unlocked).map((span) => span.token))];
  const units: MasteryUnit[] = present.map((token) => packUnit(snapshot, token));

  return {
    mode,
    language: snapshot.language,
    layoutId: PACK_LAYOUT,
    group,
    completedGroupIds: completedGroups(snapshot, mode).map((completed) => completed.id),
    totalGroups: packGroups(mode).length,
    totalTokens: packGroups(mode).reduce((count, item) => count + item.tokens.length, 0),
    unlockedTokens: unlocked,
    units,
    exercise,
    // Подсказки — по всему тексту занятия: под Shift на латинской раскладке
    // лежит половина пакета, и искать его глазами пользователь не должен.
    shiftHints: shiftHints(exercise.text),
    hintLevel: HINT_LEVEL[mode],
    finished: group === null,
  };
}
